import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back to PayFlex");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 p-12 text-white grid-bg">
        <Logo light />
        <div>
          <h2 className="font-display text-4xl font-extrabold leading-tight">Verify before you pay.</h2>
          <p className="text-slate-400 mt-4 max-w-md">Every payroll run flows through back-end verification. Track time, compute wages, and disburse with confidence.</p>
        </div>
        <span className="text-slate-600 text-sm">© 2026 PayFlex Portal</span>
      </div>

      <div className="flex items-center justify-center p-6 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up" data-testid="login-form">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-display text-2xl font-extrabold">Sign in</h1>
          <p className="text-slate-500 text-sm mt-1 mb-8">Access your payroll portal.</p>

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</Label>
              <Input data-testid="login-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</Label>
              <Input data-testid="login-password-input" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 h-11" />
            </div>
          </div>

          <Button type="submit" disabled={loading} data-testid="login-submit-button"
            className="w-full mt-6 h-11 bg-sky-600 hover:bg-sky-700 rounded-xl font-semibold">
            {loading ? "Signing in…" : "Sign in"}
          </Button>

          <p className="text-center text-sm text-slate-500 mt-6">
            Accounts are created by your PayFlex administrator.
          </p>
        </form>
      </div>
    </div>
  );
}
